import { defineStore } from 'pinia'
import { computed, reactive, Ref, ref, toRaw, watch } from 'vue'
import { isEqual } from 'lodash'

import { FragmentType, useFragment } from '@/gql'
import {
  JobEndTimeframe,
  JobFormDataFragment,
  JobStartTimeframe,
  JobVisibility,
  LanguageInput,
  LocationPreference,
  RateType,
  UpdateJobInput,
} from '@/gql/graphql'
import { deepToRaw } from '@/utils/deepToRaw'

import { mapCustomFieldValueToCustomFieldValueInput } from '@/components/Fieldable/utils'
import { BUDGET_TYPE, JOB_TAB } from '@/components/Job/constants'
import { JobLanguageFragment } from '@/components/Job/graphql/fragments'
import { JobForm } from '@/components/Job/hiring-types'

const getInitialFormState = (): JobForm => ({
  // Overview tab
  name: '',
  description: '',
  expectedExperienceLevel: [],
  skills: [],
  industries: [],
  customFieldValues: [],
  languages: [],
  attachments: [],

  // Details tab
  startDateTimeframe: undefined,
  startDate: undefined,
  commitment: undefined,
  endDateTimeframe: undefined,
  endDate: undefined,
  isExtensionAvailable: false,
  evaluationPeriod: undefined,
  locationPreference: LocationPreference.RemoteOnly,
  address: '',
  postCode: '',
  city: '',
  state: '',
  country: '',

  // Budget tab
  rateType: RateType.Hourly,
  budgetType: BUDGET_TYPE.SPECIFIC_RATE,
  rate: '0.00',
  minimumRate: '0.00',
  maximumRate: '0.00',
  internalBudget: '0.00',

  // Settings tab
  requiredWorkers: 1,
  jobOwners: [],
})

export const useJobFormStore = defineStore('JobFormStore', () => {
  // ---- STATE ----
  const activeTabKey = ref(JOB_TAB.OVERVIEW)
  const persistedJob = ref<JobFormDataFragment | null>(null)
  const persistedJobForm = ref<JobForm | null>(null)
  const form = reactive<JobForm>(getInitialFormState())
  const submitTriggeredOn = ref<string | null>(null)
  const formIsDirty = ref<boolean>(false)
  const totalChanges = ref<number>(0)
  const canBePublished = ref<boolean>(false)

  const hasPersistedJob = computed<boolean>(() => persistedJob.value !== null)
  const hasBeenTouched = computed<boolean>(() => submitTriggeredOn.value !== null)

  watch(form, (newValue) => {
    const compareTo = toRaw(newValue)
    const compareFrom = hasPersistedJob.value
      ? toRaw(persistedJobForm.value)
      : getInitialFormState()

    formIsDirty.value = !isEqual(compareFrom, compareTo)

    let totalChanged = 0

    if (compareFrom) {
      Object.keys(compareFrom).forEach((k) => {
        if (k === 'customFieldValues') {
          form.customFieldValues.forEach((j, index) => {
            const valueFrom = Object.values(compareFrom[k][index] ?? [])[0]?.value
            const valueTo = Object.values(compareTo[k][index] ?? [])[0]?.value

            totalChanged += valueFrom !== valueTo ? 1 : 0
          })
        } else if (Array.isArray(compareFrom[k])) {
          totalChanged += compareFrom[k].length !== compareTo[k].length ? 1 : 0
        } else {
          totalChanged += !isEqual(compareFrom[k], compareTo[k]) ? 1 : 0
        }
      })
    }

    totalChanges.value = totalChanged
  })

  // ---- UTILS   ----
  const setSubmitTrigger = (trigger: string) => {
    submitTriggeredOn.value = trigger
  }

  const resetSubmitTrigger = () => {
    submitTriggeredOn.value = null
  }

  const resetFormIsDirty = () => {
    formIsDirty.value = false
    totalChanges.value = 0
  }

  const mapLanguage = (languages: FragmentType<typeof JobLanguageFragment>[]): LanguageInput[] =>
    languages.map((language: FragmentType<typeof JobLanguageFragment>): LanguageInput => {
      const lang = useFragment(JobLanguageFragment, language)
      return {
        name: lang.name,
        experience: lang.experience,
      }
    })
  // Transform some persisted job values to the format components expect
  const getPersistedJobAsForm = (newPersistedJob: Ref<JobFormDataFragment>): JobForm => {
    const initialFormState = getInitialFormState()

    canBePublished.value = newPersistedJob.value?.canBePublished

    return deepToRaw({
      name: persistedJob.value?.name!,
      description: persistedJob.value?.description! ?? '',
      expectedExperienceLevel: newPersistedJob.value?.expectedExperienceLevel ?? [],
      skills: newPersistedJob.value.skills.map((skill) => skill.name),
      industries: newPersistedJob.value.industries!.map((industry) => ({
        name: industry.name.toLowerCase(),
        id: industry.id,
      })),
      customFieldValues: mapCustomFieldValueToCustomFieldValueInput(
        newPersistedJob.value?.customFieldValues ?? [],
      ),
      languages: mapLanguage(newPersistedJob.value.languages ?? []),
      attachments: newPersistedJob.value.attachments,
      startDate: newPersistedJob.value.startDate,
      startDateTimeframe:
        newPersistedJob.value.startDateTimeframe ?? initialFormState.startDateTimeframe,
      commitment: newPersistedJob.value.association!,
      endDate: newPersistedJob.value.endDate,
      endDateTimeframe: newPersistedJob.value.endDateTimeframe ?? initialFormState.endDateTimeframe,
      isExtensionAvailable: newPersistedJob.value?.isExtensionAvailable ?? false,
      evaluationPeriod:
        newPersistedJob.value?.evaluationPeriod ?? initialFormState.evaluationPeriod,
      locationPreference:
        newPersistedJob.value?.locationPreference ?? LocationPreference.OnsiteOnly,
      address: newPersistedJob.value?.location?.address ?? newPersistedJob.value?.address,
      city: newPersistedJob.value?.location?.city,
      postCode: newPersistedJob.value?.location?.postCode,
      country: newPersistedJob.value?.location?.country?.isoCode,
      state: newPersistedJob.value?.location?.state,
      rateType: newPersistedJob.value?.rateType ?? RateType.Hourly,
      budgetType:
        newPersistedJob.value?.minimumRate || newPersistedJob.value?.maximumRate
          ? BUDGET_TYPE.RATE_RANGE
          : BUDGET_TYPE.SPECIFIC_RATE,
      rate: newPersistedJob.value?.rate?.toString() ?? initialFormState.rate,
      minimumRate: newPersistedJob.value?.minimumRate?.toString() ?? initialFormState.minimumRate,
      maximumRate: newPersistedJob.value?.maximumRate?.toString() ?? initialFormState.maximumRate,
      internalBudget:
        newPersistedJob.value?.internalBudget?.toString() ?? initialFormState.internalBudget,
      isForMarketplace:
        newPersistedJob.value.visibility.indexOf(JobVisibility.VisibleForMarketplace) > -1,
      requiredWorkers: newPersistedJob.value?.requiredWorkers ?? initialFormState.requiredWorkers,
      jobOwners: newPersistedJob.value?.owners.map(({ avatar, id, name }) => ({
        label: name,
        value: id,
        avatar,
      })),
    })
  }
  const mapPersistedJobToForm = () => {
    if (!hasPersistedJob.value) {
      Object.assign(form, getInitialFormState())
      resetFormIsDirty()
      return
    }

    persistedJobForm.value = getPersistedJobAsForm(persistedJob as Ref<JobFormDataFragment>)
    Object.assign(form, persistedJobForm.value)
    resetFormIsDirty()
  }

  // ---- ACTIONS ----
  const loadPersistedJob = (newPersistedJob: JobFormDataFragment) => {
    persistedJob.value = newPersistedJob
    mapPersistedJobToForm()
  }

  const getFormInputPayload = (): UpdateJobInput => {
    let payload = {}

    payload = {
      // Overview tab
      name: form.name,
      description: form.description,
      expectedExperienceLevel: form.expectedExperienceLevel,
      skills: form.skills,
      ...(form.industries && {
        industries: form.industries.map((industry) => industry.id),
      }),
      ...(form.attachments && {
        attachments: form.attachments.map((attachment) => attachment.id),
      }),
      ...(form.languages && { languages: form.languages }),

      // Transform custom field values into the format expected by the mutation, eg:
      // { freeText: {id, value}, singleSelect: {id, value} }
      ...(form.customFieldValues && {
        customFieldValues: form.customFieldValues,
      }),

      // Details tab
      startDateTimeframe: form.startDateTimeframe,
      ...(form.startDateTimeframe === JobStartTimeframe.OnDate && { startDate: form.startDate }),
      association: form.commitment,
      endDateTimeframe: form.endDateTimeframe,
      ...(form.endDateTimeframe === JobEndTimeframe.OnDate && { endDate: form.endDate }),
      isExtensionAvailable: form.isExtensionAvailable,
      evaluationPeriod: form.evaluationPeriod,
      locationPreference: {
        preference: form.locationPreference,
        address: form.address,
      },
      location: {
        address: form.address,
        postCode: form.postCode,
        city: form.city,
        country: form.country,
        state: form.state,
      },

      // Budget tab
      ...(form.rate !== '0.00' &&
        form.budgetType === BUDGET_TYPE.SPECIFIC_RATE && {
          rateType: { type: form.rateType, rate: parseFloat(form.rate) },
        }),
      ...((form.minimumRate !== '0.00' || form.maximumRate !== '0.00') &&
        form.budgetType === BUDGET_TYPE.RATE_RANGE && {
          rateType: {
            type: form.rateType,
            range: {
              minimum: parseFloat(form.minimumRate!),
              maximum: parseFloat(form.maximumRate!),
            },
          },
        }),

      // Settings tab
      requiredWorkers: form.requiredWorkers,
      ...(form.jobOwners?.length && {
        owners: form.jobOwners.map(({ value }) => value),
      }),
    }

    return {
      id: persistedJob.value!.id,
      ...payload,
    }
  }

  return {
    // State
    persistedJob,
    form,
    activeTabKey,
    formIsDirty,
    submitTriggeredOn,
    totalChanges,
    canBePublished,

    // Getters
    hasPersistedJob,
    hasBeenTouched,

    // Actions
    loadPersistedJob,
    getFormInputPayload,
    setSubmitTrigger,
    resetSubmitTrigger,
  }
})
